import { RawMaterialAnalysis, SchaefflerBomItem } from '../models';
import { UnitOfMeasure } from '../models/unit-of-measure.model';

export const extractRawMaterials = (
  bomItems: SchaefflerBomItem[]
): SchaefflerBomItem[] =>
  bomItems?.filter(
    (item) =>
      item.materialCharacteristics?.type === 'ROH' && item.itemCategory === 'M'
  );

export const aggregateRawMaterials = (
  rawMaterialBomItems: SchaefflerBomItem[]
): RawMaterialAnalysis[] => {
  const aggregatedRawMaterials: Map<string, RawMaterialAnalysis> = new Map();

  rawMaterialBomItems.forEach((bomItem) => {
    if (aggregatedRawMaterials.has(bomItem.materialDesignation)) {
      // aggregate existing
      const rawMaterialAnalysis = aggregatedRawMaterials.get(
        bomItem.materialDesignation
      );
      rawMaterialAnalysis.operatingUnit += bomItem.quantities.quantity;
      rawMaterialAnalysis.totalCosts += bomItem.costing.costAreaTotalValue;
    } else {
      const rawMaterialAnalysis: RawMaterialAnalysis = {
        materialDesignation: bomItem.materialDesignation,
        materialNumber: bomItem.materialNumber,
        costShare: undefined,
        supplier: bomItem.procurement.vendorDescription,
        operatingUnit: bomItem.quantities.quantity,
        unitOfMeasure:
          UnitOfMeasure[
            bomItem.quantities.baseUnitOfMeasure as keyof typeof UnitOfMeasure
          ] || UnitOfMeasure.UNRECOGNISED,
        uomBaseToPriceFactor:
          bomItem.materialCharacteristics.uomBaseToPriceFactor,
        price: undefined,
        totalCosts: bomItem.costing.costAreaTotalValue,
        totalPrice: bomItem.costing.costAreaTotalPrice,
        currency: bomItem.costing.costAreaCurrency,
      };

      rawMaterialAnalysis.unrecognisedUOM =
        rawMaterialAnalysis.unitOfMeasure === UnitOfMeasure.UNRECOGNISED
          ? bomItem.quantities.baseUnitOfMeasure
          : '';

      aggregatedRawMaterials.set(
        rawMaterialAnalysis.materialDesignation,
        rawMaterialAnalysis
      );
    }
  });

  return [...aggregatedRawMaterials.values()];
};

export const calculateCostAndPrice = (
  aggregatedRawMaterials: RawMaterialAnalysis[],
  costAreaTotalValue: number
): RawMaterialAnalysis[] =>
  aggregatedRawMaterials.map((analysis) => ({
    ...analysis,
    costShare: analysis.totalCosts / costAreaTotalValue,
    price: calculatePricePerUnit(
      analysis.totalCosts,
      analysis.operatingUnit,
      analysis.unitOfMeasure,
      analysis.uomBaseToPriceFactor,
      analysis.totalPrice
    ),
  }));

const calculatePricePerUnit = (
  totalCosts: number,
  operatingUnit: number,
  unitOfMeasure: UnitOfMeasure,
  uomBaseToPriceFactor: number,
  totalPrice: number
): number => {
  switch (unitOfMeasure) {
    case UnitOfMeasure.G: {
      return operatingUnit === 0 ? 0 : (totalCosts / operatingUnit) * 1000;
    }
    case UnitOfMeasure.KG: {
      return operatingUnit === 0 ? 0 : totalCosts / operatingUnit;
    }
    case UnitOfMeasure.M: {
      return totalPrice * uomBaseToPriceFactor;
    }
    case UnitOfMeasure.MM: {
      return totalPrice * uomBaseToPriceFactor;
    }
    default: {
      return 0;
    }
  }
};

export const calculateRawMaterialsSummary = (
  data: RawMaterialAnalysis[]
): RawMaterialAnalysis[] | undefined =>
  data?.length > 0
    ? [
        {
          materialDesignation: undefined,
          materialNumber: undefined,
          costShare: undefined,
          supplier: undefined,
          operatingUnit: undefined,
          unitOfMeasure: undefined,
          uomBaseToPriceFactor: undefined,
          price: undefined,
          currency: data[0].currency,
          totalCosts: data
            .map((item) => item.totalCosts)
            .reduce(
              (current: number, previous: number) => previous + current,
              0
            ),
          totalPrice: undefined,
        },
      ]
    : undefined;
